import useMergedRef from '@react-hook/merged-ref';
import { ELEMENT_OL, ELEMENT_UL } from '@udecode/plate';
import { classNamesFunction, styled } from '@uifabric/utilities';
import cn from 'classnames';
import * as React from 'react';

import { ELEMENT_MULTI_COL } from '../../multi-col';
import { useDndBlock } from '../hooks';
import { getDraggableStyles } from './Draggable.styles';

const { useRef, useState, useMemo } = React;

const getClassNames = classNamesFunction<any, any>();

// Toggle Multi Col feature
export const isSideDropEnabled = false;

export const allSides = isSideDropEnabled ? ['top', 'bottom', 'left', 'right'] : ['top', 'bottom'];

export const DraggableBase = ({
  children,
  element,
  className,
  styles,
  componentRef,
  onRenderDragHandle: DragHandle,
}: any): JSX.Element => {
  const blockRef = useRef<HTMLDivElement>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const nullRef = useRef(null);
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const dragWrapperRef = useRef(null);
  const multiRootRef = useMergedRef(componentRef, rootRef);

  const { dropLine, dragRef, isDragging } = useDndBlock({
    id: element.id,
    blockRef: element.type === ELEMENT_MULTI_COL ? nullRef : rootRef,
  });

  const multiDragRef = useMergedRef(dragRef, dragWrapperRef);

  const isValidHover = useMemo(() => {
    if (element.type === ELEMENT_MULTI_COL && dropLine && allSides.includes(dropLine)) return false;
    return true;
  }, [element, dropLine]);

  const classNames: any = getClassNames(styles, {
    className,
    direction: dropLine,
    isDragging,
  });

  return (
    <div
      className={cn('relative slate-Draggable', {
        'opacity-50': isDragging,
        'bg-blue-100': isMenuOpened,
        // TODO: scope the css only inside the multi col layout
        'pl-10 pr-35 editor-dragging-block': element.type !== ELEMENT_MULTI_COL,
        'leading-30': [ELEMENT_UL, ELEMENT_OL].includes(element.type),
      })}
      data-type={element.type}
      ref={multiRootRef}
    >
      <div
        ref={blockRef}
        className={cn({
          'border-blue-500': !!dropLine,
          'border-t-2': dropLine === 'top' && isValidHover,
          'border-b-2': dropLine === 'bottom' && isValidHover,
          'border-l-2': dropLine === 'left' && isValidHover && isSideDropEnabled,
          'border-r-2': dropLine === 'right' && isValidHover && isSideDropEnabled,
        })}
      >
        {children}
      </div>

      <div
        className={cn(
          'slate-gutter-left absolute top-0 transform -translate-x-full editor-block-toolbar',
          classNames.gutterLeft,
          classNames.blockAndGutter,
          { 'override:flex': isMenuOpened }
        )}
        contentEditable={false}
      >
        <div className={cn('flex items-start')}>
          <div className={cn('mr-4 flex items-center')} ref={multiDragRef}>
            {DragHandle && (
              <DragHandle
                element={element}
                className={cn(
                  'min-w-18 h-18 p-0 bg-transparent bg-no-repeat border-none cursor-pointer overflow-hidden outline-none'
                )}
                onMouseDown={(e: any) => e.stopPropagation()}
                setIsMenuOpened={setIsMenuOpened}
                isMenuOpened={isMenuOpened}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const Draggable = styled<any, any, any>(DraggableBase, getDraggableStyles, undefined, {
  scope: 'Draggable',
});
